/* eslint-disable complexity, max-lines */
import { DataList, type TableColumn, type DeleteItemContext, type ActionConfig } from '@core/components/DataList';
import {
  TextInput,
  Search,
  Card,
  IconButton,
  Dropdown,
  Ascending,
  Descending,
  ButtonType,
  IconSize,
  OverlayMode,
  Text,
  ChevronLeft,
  ChevronRight,
} from '@soteria/design-system-ve';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import * as styles from './SortableDataList.css';

export interface SortableTableColumn<T> extends TableColumn<T> {
  sortable?: boolean;
  sortKey?: keyof T | ((item: T) => string | number);
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  key: keyof T | ((item: T) => string | number);
  direction: SortDirection;
}

export interface SortOption<T> {
  value: keyof T | string;
  label: string;
  sortKey?: keyof T | ((item: T) => string | number);
}

export interface SortableDataListProps<T> {
  data: T[];
  columns: SortableTableColumn<T>[];
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  additionalAction?: ActionConfig<T>;
  isLoading?: boolean;
  dataTypeName: string;
  variant?: 'default' | 'condensed';
  hideEmptyState?: boolean;
  loadingRowCount?: number;
  emptyStateMessage?: string;
  error?: Error | string | null | unknown | Array<{ correlationId?: string }>;
  deleteItemContext?: DeleteItemContext<T>;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKey?: keyof T | ((item: T) => string);
  defaultSort?: SortConfig<T>;
  onSortChange?: (sort: SortConfig<T> | null) => void;
  // New props for consistent filter bar
  showFilterBar?: boolean;
  sortOptions?: SortOption<T>[];
  // Pagination props
  enablePagination?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

/**
 * Helper function to get sortable value from an item
 */
function getSortValue<T>(item: T, sortKey: keyof T | ((item: T) => string | number)): string | number {
  if (typeof sortKey === 'function') {
    return sortKey(item);
  }

  const value = item[sortKey];

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return String(value || '');
}

/**
 * Helper function to get searchable value from an item
 */
function getSearchValue<T>(item: T, searchKey?: keyof T | ((item: T) => string)): string {
  if (!searchKey) {
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;

      return (obj.name as string) || (obj.labelName as string) || '';
    }

    return String(item || '');
  }

  if (typeof searchKey === 'function') {
    return searchKey(item);
  }

  const value = item[searchKey];

  return String(value || '');
}

/**
 * Enhanced sortable and searchable data list component
 */
// eslint-disable-next-line max-statements
export function SortableDataList<T>({
  data,
  columns,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchKey,
  defaultSort,
  onSortChange,
  showFilterBar = false,
  sortOptions = [],
  enablePagination = false,
  defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  ...dataListProps
}: SortableDataListProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(defaultSort || null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const tableRef = useRef<HTMLDivElement>(null);

  // Handle search - use current sort field as search field if available
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase();

    // Use the current sort field as the search field if available, otherwise fall back to searchKey
    const currentSearchKey = sortConfig?.key || searchKey;

    return data.filter(item => {
      let searchValue: string;

      if (currentSearchKey && typeof currentSearchKey === 'function') {
        // Handle function-based keys by converting result to string
        searchValue = String(currentSearchKey(item));
      } else if (currentSearchKey) {
        // Handle property-based keys
        searchValue = String(item[currentSearchKey as keyof T] || '');
      } else {
        // Fallback to default search behavior
        searchValue = getSearchValue(item, searchKey);
      }

      return searchValue.toLowerCase().includes(searchLower);
    });
  }, [data, searchTerm, searchable, searchKey, sortConfig]);

  // Handle sorting
  const sortedAndFilteredData = useMemo(() => {
    if (!sortConfig) {
      return filteredData;
    }

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      let comparison = 0;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Calculate pagination values
  const totalItems = sortedAndFilteredData.length;
  const totalPages = enablePagination ? Math.ceil(totalItems / pageSize) : 1;

  // Apply pagination
  const paginatedData = useMemo(() => {
    if (!enablePagination) {
      return sortedAndFilteredData;
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return sortedAndFilteredData.slice(startIndex, endIndex);
  }, [sortedAndFilteredData, enablePagination, currentPage, pageSize]);

  // Reset to page 1 when search term or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  // Handle sort column click (for header clicking)
  const handleSort = useCallback(
    (column: SortableTableColumn<T>) => {
      if (!column.sortable) return;
      const sortKey = column.sortKey || (column.key as keyof T);
      const newSortConfig: SortConfig<T> = {
        key: sortKey,
        direction: sortConfig?.key === sortKey && sortConfig.direction === 'asc' ? 'desc' : 'asc',
      };

      setSortConfig(newSortConfig);
      onSortChange?.(newSortConfig);
    },
    [sortConfig, onSortChange]
  );

  // Handle sort dropdown change (for filter bar)
  const handleSortFieldChange = useCallback(
    (selectedValue: string) => {
      const sortOption = sortOptions.find(opt => opt.value === selectedValue);

      if (!sortOption) return;

      const sortKey = sortOption.sortKey || (sortOption.value as keyof T);
      const newSortConfig: SortConfig<T> = {
        key: sortKey,
        direction: sortConfig?.direction || 'asc',
      };

      setSortConfig(newSortConfig);
      onSortChange?.(newSortConfig);
    },
    [sortOptions, sortConfig, onSortChange]
  );

  // Handle sort direction toggle (for filter bar)
  const handleSortDirectionToggle = useCallback(() => {
    if (!sortConfig) return;
    const newSortConfig: SortConfig<T> = {
      key: sortConfig.key,
      direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
    };

    setSortConfig(newSortConfig);
    onSortChange?.(newSortConfig);
  }, [sortConfig, onSortChange]);

  // Add click handlers to table headers after render (for header clicking)
  useEffect(() => {
    if (showFilterBar || !tableRef.current) return; // Skip if using filter bar

    const table = tableRef.current.querySelector('table');

    if (!table) return;

    const headers = table.querySelectorAll('thead th');
    const clickHandlers: Array<(event: Event) => void> = [];

    headers.forEach((header, index) => {
      const column = columns[index];

      if (column?.sortable) {
        const handler = (event: Event) => {
          event.preventDefault();
          handleSort(column);
        };

        (header as HTMLElement).style.cursor = 'pointer';
        (header as HTMLElement).style.userSelect = 'none';
        header.addEventListener('click', handler);
        clickHandlers.push(handler);
      }
    });

    return () => {
      headers.forEach((header, index) => {
        if (clickHandlers[index]) {
          header.removeEventListener('click', clickHandlers[index]);
        }
      });
    };
  }, [columns, handleSort, sortedAndFilteredData, showFilterBar]);

  // Transform columns to add sorting indicators (only if not using filter bar)
  const enhancedColumns: TableColumn<T>[] = useMemo(() => {
    if (showFilterBar) {
      return columns; // Don't modify headers if using filter bar
    }

    return columns.map(column => {
      if (!column.sortable) {
        return column;
      }

      const isSorted = sortConfig?.key === (column.sortKey || column.key);
      const sortDirection = isSorted ? sortConfig?.direction : null;
      const sortIndicator = isSorted ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : ' ↕';

      return {
        ...column,
        header: `${column.header}${sortIndicator}`,
      };
    });
  }, [columns, sortConfig, showFilterBar]);

  // Get current sort field for dropdown
  const currentSortField = useMemo(() => {
    if (!sortConfig || !sortOptions.length) return '';
    const option = sortOptions.find(opt => {
      const optionKey = opt.sortKey || opt.value;

      return optionKey === sortConfig.key;
    });

    return option ? String(option.value) : '';
  }, [sortConfig, sortOptions]);

  // Get dynamic placeholder text based on selected field
  const dynamicPlaceholder = useMemo(() => {
    if (!sortConfig || !sortOptions.length) {
      return searchPlaceholder;
    }

    const option = sortOptions.find(opt => {
      const optionKey = opt.sortKey || opt.value;

      return optionKey === sortConfig.key;
    });

    return option ? `Search by ${option.label.toLowerCase()}...` : searchPlaceholder;
  }, [sortConfig, sortOptions, searchPlaceholder]);

  return (
    <div>
      {/* Filter Bar */}
      {(showFilterBar || searchable || sortOptions.length > 0 || enablePagination) && (
        <Card padding="medium" style={{ marginBottom: '16px' }}>
          <div className={styles.mainFilterBar}>
            <div className={styles.filterBarLeft}>
              {searchable && (
                <TextInput
                  placeholder={dynamicPlaceholder}
                  prefixIcon={Search}
                  style={{ minWidth: '250px', paddingLeft: '8px' }}
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              )}
            </div>

            <div className={styles.controlGroup}>
              {/* Sort Direction Toggle */}
              {sortConfig && (
                <IconButton
                  aria-label={`Sort ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`}
                  buttonType={ButtonType.Secondary}
                  disabled={dataListProps.isLoading}
                  icon={sortConfig.direction === 'asc' ? Ascending : Descending}
                  iconSize={IconSize.Small}
                  overlayMode={OverlayMode.Solid}
                  title={`Sort ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'}`}
                  onClick={handleSortDirectionToggle}
                />
              )}

              {/* Sort Field Dropdown */}
              {sortOptions.length > 0 && (
                <Dropdown
                  disabled={dataListProps.isLoading}
                  options={sortOptions.map(opt => ({ id: String(opt.value), value: String(opt.value), label: opt.label }))}
                  placeholder="Sort by..."
                  selectedItem={currentSortField}
                  size="medium"
                  onSelectionChange={handleSortFieldChange}
                />
              )}

              {/* Page Size Dropdown - Top Right */}
              {enablePagination && (
                <Dropdown
                  disabled={dataListProps.isLoading}
                  options={pageSizeOptions.map(size => ({ id: String(size), value: String(size), label: `${size} per page` }))}
                  placeholder="Items per page"
                  selectedItem={String(pageSize)}
                  size="medium"
                  onSelectionChange={handlePageSizeChange}
                />
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Data List */}
      <div ref={tableRef}>
        <DataList {...dataListProps} columns={enhancedColumns} data={paginatedData} />
      </div>

      {/* Pagination Controls - Center Aligned */}
      {enablePagination && totalPages > 1 && (
        <div className={styles.paginationCenter}>
          <IconButton
            aria-label="Previous page"
            buttonType={ButtonType.Secondary}
            disabled={currentPage === 1 || dataListProps.isLoading}
            icon={ChevronLeft}
            iconSize={IconSize.Small}
            title="Previous page"
            onClick={() => handlePageChange(currentPage - 1)}
          />
          <Text>
            Page {currentPage} of {totalPages}
          </Text>
          <IconButton
            aria-label="Next page"
            buttonType={ButtonType.Secondary}
            disabled={currentPage === totalPages || dataListProps.isLoading}
            icon={ChevronRight}
            iconSize={IconSize.Small}
            title="Next page"
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </div>
      )}
    </div>
  );
}
